import { Card, Stack } from '@mui/material';
import React from 'react';
import { match } from 'ts-pattern';
import ChatContext from './chat-context';
import ChatMessagePart from './chat-message-part';
import ChatStopButton from './chat-stop-button';
import type {
  ChatContextItem,
  ChatMessageAnnotation,
  UIMessage,
} from './types';
import type { UseChatResult } from './use-chat';

export interface ChatMessageProps {
  message: UIMessage;
  inputMessage?: UIMessage;
  status: UseChatResult['status'];
  onStop: UseChatResult['stop'];
}

function ChatMessage({
  message,
  inputMessage,
  status,
  onStop,
}: ChatMessageProps) {
  const messageContext = React.useMemo(() => {
    return getContextFromMessage(message);
  }, [message]);

  return (
    <div key={message.id}>
      {match(message)
        .with({ role: 'user' }, (msg) => (
          <React.Fragment>
            <Card className='px-4 pb-1 pt-2 min-h-full w-full relative border-0'>
              <ChatContext context={messageContext} disabled />
              <Stack direction='row' spacing={1} alignItems='flex-start'>
                {msg.parts?.map((part, i) => (
                  <div key={part.type + i} className='py-3 w-full'>
                    <ChatMessagePart messageId={message.id} part={part} />
                  </div>
                ))}
              </Stack>
              {['submitted', 'streaming'].includes(status) &&
                inputMessage?.id === message.id && (
                  <div className='w-full flex items-center justify-center gap-1 mb-2'>
                    <div className='loading loading-dots loading-sm' />
                    <div>Generating</div>
                    <div className='flex-1' />
                    <ChatStopButton onClick={onStop} size='small' />
                  </div>
                )}
            </Card>
          </React.Fragment>
        ))
        .with({ role: 'assistant' }, (msg) => (
          <div className='w-full pl-3 *:w-full *:mb-3 *:block'>
            {msg.parts?.map((part, i) => (
              <ChatMessagePart
                key={part.type + i}
                messageId={message.id}
                part={part}
              />
            ))}
          </div>
        ))
        .otherwise(() => null)}
    </div>
  );
}

export default ChatMessage;

/*
|------------------
| Internal
|------------------
*/

function getContextFromMessage(message: UIMessage): ChatContextItem[] {
  if (message.role !== 'user') {
    return [];
  }

  const annotations = (message.annotations ??
    []) as unknown as ChatMessageAnnotation[];
  const foundContext = annotations.find((a) => a.type === 'context');

  if (!foundContext) {
    return [];
  }

  return [
    ...foundContext.context.githubRepositories,
    ...foundContext.context.githubRepositoryFiles,
  ];
}
